/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { useMedico } from '../../../hooks/medico';

import Input from '../../../components/Input';
import SelectSync from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';
import HeaderRelatorio from '../HeaderRelatorio';

interface IRelatorio {
  UsuCodigo: number;
  UsuNome: string;
  Login: Date;
  LoginFormatado: string;
  PrimeiroAtendimento: Date | null;
  PrimeiroAtendimentoFormatado: string;
  UltimoAtendimento: Date | null;
  UltimoAtendimentoFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  UsuCodigo: number;
  soMedicos: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const optionsTipo = [
  { value: 'false', label: 'TODOS' },
  { value: 'true', label: 'MÉDICOS' },
];

const RegistroLoginDia: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsMedico, buscaOptionsMedico } = useMedico();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio[]>();

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
    soMedicos: 'false',
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Exibe a data e hora do primeiro acesso ao sistema, em caso de médicos, exibe também o horário de início do primeiro atendimento e o final do último atendimento em um determinado período.`;

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      await buscaOptionsMedico();
    }
    if (!optionsMedico || Object.keys(optionsMedico).length === 0)
      loadMedicos();
  }, [buscaOptionsMedico, optionsMedico]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        setDadosRelatorio(undefined);
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          UsuCodigo: Yup.string().nullable(true),
          soMedicos: Yup.boolean().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        let parametrosOpcionais = dados.UsuCodigo
          ? `&UsuCodigo=${dados.UsuCodigo}`
          : '';
        parametrosOpcionais = dados.soMedicos
          ? `&soMedicos=${dados.soMedicos}`
          : '';

        const response = await api.get<IRelatorio[]>(
          `/relatorios/registrologindia?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        const responseFormatado = response.data.map(rel => {
          return {
            ...rel,
            LoginFormatado: format(
              parseISO(rel.Login.toString()),
              'dd/MM/yyyy HH:mm:ss',
            ),
            PrimeiroAtendimentoFormatado:
              rel.PrimeiroAtendimento === null
                ? ''
                : format(
                    parseISO(rel.PrimeiroAtendimento.toString()),
                    'dd/MM/yyyy HH:mm:ss',
                  ),
            UltimoAtendimentoFormatado:
              rel.UltimoAtendimento === null
                ? ''
                : format(
                    parseISO(rel.UltimoAtendimento.toString()),
                    'dd/MM/yyyy HH:mm:ss',
                  ),
          };
        });

        setDadosRelatorio(responseFormatado);
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Registro de Login e Atendimento</h2>
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.SUPERVISOR}
            />
          </div>
          {user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={2}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={2}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={2}>
                  <SelectSync
                    name="soMedicos"
                    label="Tipo"
                    options={optionsTipo}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={2}>
                  <SelectSync
                    name="UsuCodigo"
                    label="Médico"
                    options={optionsMedico}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={1}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`REGISTRO LOGIN ${dataExibe?.dataDiaSemana || ''}`}
        />

        <div className="bg-white border p-2" style={{ minHeight: '300px' }}>
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '100%' }}>Usuário</th>
                    <th style={{ minWidth: '150px' }}>Login Inicial</th>
                    <th style={{ minWidth: '150px' }}>Primeiro Atendimento</th>
                    <th style={{ minWidth: '150px' }}>Ultimo Atendimento</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <tr
                        key={`loginUsu-${rel.UsuCodigo}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>{rel.UsuNome}</td>
                        <td>{rel.LoginFormatado}</td>
                        <td>{rel.PrimeiroAtendimentoFormatado}</td>
                        <td>{rel.UltimoAtendimentoFormatado}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default RegistroLoginDia;
